import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';

const typeOptions = ['FIXED', 'FLEX'];
const statusOptions = ['ACTIVE', 'INACTIVE'];
const dayOptions = [
  { value: 'MONDAY', label: 'Lunes' },
  { value: 'TUESDAY', label: 'Martes' },
  { value: 'WEDNESDAY', label: 'Miercoles' },
  { value: 'THURSDAY', label: 'Jueves' },
  { value: 'FRIDAY', label: 'Viernes' },
  { value: 'SATURDAY', label: 'Sabado' },
  { value: 'SUNDAY', label: 'Domingo' },
];
const dayValues = dayOptions.map((option) => option.value);

const emptyRule = { dayOfWeek: 'MONDAY', startTime: '08:00', endTime: '17:00', breakMinutes: 60 };

export default function WorkScheduleForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [departments, setDepartments] = useState([]);
  const [isDepartmentsLoading, setIsDepartmentsLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'FIXED',
    status: 'ACTIVE',
    departmentId: '',
  });
  const [rules, setRules] = useState([{ ...emptyRule }]);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSchedule = async () => {
      if (!isEdit) {
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/schedules/${id}`);
        const schedule = response.data;
        setForm({
          name: schedule.name || '',
          description: schedule.description || '',
          type: schedule.type || 'FIXED',
          status: schedule.status || 'ACTIVE',
          departmentId: schedule.departmentId ?? '',
        });
        setRules(
          schedule.rules && schedule.rules.length > 0
            ? schedule.rules.map((rule) => ({
                dayOfWeek: rule.dayOfWeek,
                startTime: rule.startTime,
                endTime: rule.endTime,
                breakMinutes: rule.breakMinutes ?? 0,
              }))
            : [{ ...emptyRule }],
        );
      } catch (err) {
        const message =
          err.response?.data?.message || 'No se pudo cargar el horario.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadSchedule();
  }, [id, isEdit]);

  useEffect(() => {
    const loadDepartments = async () => {
      setIsDepartmentsLoading(true);
      try {
        const response = await axios.get('/departments');
        setDepartments(response.data);
      } catch (err) {
        setError((prev) => prev || 'No se pudieron cargar los departamentos.');
      } finally {
        setIsDepartmentsLoading(false);
      }
    };

    loadDepartments();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRuleChange = (index, field, value) => {
    setRules((prev) =>
      prev.map((rule, idx) => (idx === index ? { ...rule, [field]: value } : rule)),
    );
  };

  const addRule = () => {
    setRules((prev) => [...prev, { ...emptyRule }]);
  };

  const duplicateWeekdays = () => {
    const baseRule = rules[0] || emptyRule;
    const weekdaySet = new Set(dayValues.slice(0, 5));
    const preserved = rules.filter((rule) => !weekdaySet.has(rule.dayOfWeek));
    const weekdayRules = Array.from(weekdaySet).map((day) => ({
      dayOfWeek: day,
      startTime: baseRule.startTime,
      endTime: baseRule.endTime,
      breakMinutes: baseRule.breakMinutes,
    }));
    setRules([...weekdayRules, ...preserved]);
  };

  const removeRule = (index) => {
    setRules((prev) => prev.filter((_, idx) => idx !== index));
  };

  const validationMessage = useMemo(() => {
    const days = rules.map((rule) => rule.dayOfWeek);
    const uniqueDays = new Set(days);
    if (uniqueDays.size !== days.length) {
      return 'Cada dia solo puede estar una vez en las reglas.';
    }
    for (const rule of rules) {
      if (!rule.startTime || !rule.endTime) {
        return 'Todos los horarios deben tener inicio y fin.';
      }
      if (rule.startTime >= rule.endTime) {
        return 'La hora de inicio debe ser menor que la hora de fin.';
      }
    }
    return null;
  }, [rules]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }
    setIsSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      type: form.type,
      status: form.status,
      departmentId: form.departmentId ? Number(form.departmentId) : undefined,
      rules: rules.map((rule) => ({
        dayOfWeek: rule.dayOfWeek,
        startTime: rule.startTime,
        endTime: rule.endTime,
        breakMinutes: Number(rule.breakMinutes) || 0,
      })),
    };

    try {
      if (isEdit) {
        await axios.patch(`/schedules/${id}`, payload);
      } else {
        await axios.post('/schedules', payload);
      }
      navigate('/home/horarios');
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo guardar el horario.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Editar horario' : 'Nuevo horario'}
        </h1>
        <p className="text-sm text-gray-500">
          Configura tipo, estado y reglas por dia.
        </p>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm">
        {isLoading ? (
          <div className="text-sm text-gray-500">Cargando horario...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-gray-600">Nombre</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Departamento</label>
                <select
                  name="departmentId"
                  value={form.departmentId}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                  disabled={isDepartmentsLoading}
                >
                  {isDepartmentsLoading ? (
                    <option value="">Cargando departamentos...</option>
                  ) : (
                    <>
                      <option value="">Sin departamento</option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.code} - {department.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Tipo</label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                >
                  {typeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Estado</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-gray-600">Descripcion</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows="3"
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">Reglas</h2>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={duplicateWeekdays}
                    className="rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Duplicar Lun-Vie
                  </button>
                  <button
                    type="button"
                    onClick={addRule}
                    className="rounded-lg border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Agregar regla
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-4">
                {rules.map((rule, index) => (
                  <div
                    key={`${rule.dayOfWeek}-${index}`}
                    className="grid gap-3 rounded-lg border border-gray-100 p-4 md:grid-cols-5"
                  >
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Dia</label>
                      <select
                        value={rule.dayOfWeek}
                        onChange={(event) => handleRuleChange(index, 'dayOfWeek', event.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm"
                      >
                        {dayOptions.map((day) => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Inicio</label>
                      <input
                        type="time"
                        value={rule.startTime}
                        onChange={(event) => handleRuleChange(index, 'startTime', event.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Fin</label>
                      <input
                        type="time"
                        value={rule.endTime}
                        onChange={(event) => handleRuleChange(index, 'endTime', event.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500">Descanso (min)</label>
                      <input
                        type="number"
                        min="0"
                        value={rule.breakMinutes}
                        onChange={(event) => handleRuleChange(index, 'breakMinutes', event.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 p-2 text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => removeRule(index)}
                        disabled={rules.length === 1}
                        className="w-full rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/home/horarios')}
                className="rounded-lg border border-gray-200 px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
